import { useEffect, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/contexts/LanguageContext";
import { OPEN_COOKIE_SETTINGS_EVENT, hasAnalyticsConsent, loadGoogleAnalytics, revokeAnalyticsConsent, setMeasurementId, trackPageView } from "@/lib/analytics";

const CookieConsent = ({ measurementId }: { measurementId: string | null }) => {
  setMeasurementId(measurementId);
  const { language } = useLanguage();
  const fr = language === "fr";
  const router = useRouter();
  // Server HTML includes the notice: no idle callback or timer can delay a first visit.
  const [showBanner, setShowBanner] = useState(true);
  const previousPage = useRef<string | null>(null);

  useEffect(() => {
    let consent: string | null = null;
    try { consent = localStorage.getItem("cookie-consent"); } catch { /* keep the choice available */ }
    setShowBanner(consent !== "accepted" && consent !== "declined");
    if (hasAnalyticsConsent()) loadGoogleAnalytics();
    const reopen = () => setShowBanner(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  useEffect(() => {
    previousPage.current = router.state.location.href;
    return router.subscribe("onResolved", () => {
      const page = router.state.location.href;
      if (previousPage.current === page) return;
      previousPage.current = page;
      // Wait for the new page's head metadata to reach the document.
      requestAnimationFrame(() => trackPageView());
    });
  }, [router]);

  const choose = (value: "accepted" | "declined") => {
    // Withdrawal after acceptance: clear the acceptance, stop GA and delete its cookies, then remember the refusal.
    if (value === "declined" && hasAnalyticsConsent()) revokeAnalyticsConsent();
    try { localStorage.setItem("cookie-consent", value); } catch { return; }
    setShowBanner(false);
    if (value === "accepted") loadGoogleAnalytics();
  };

  if (!showBanner) return null;

  return (
    <section aria-label={fr ? "Préférences cookies" : "Cookie preferences"} className="cookie-notice fixed inset-x-0 bottom-0 z-50 border-t border-input bg-background text-foreground">
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-snug">
          {fr ? "Cookies de mesure d’audience, uniquement avec votre accord. " : "Audience measurement cookies, only with your consent. "}
          <Link to="/privacy" className="underline underline-offset-2">{fr ? "Confidentialité" : "Privacy"}</Link>
        </p>
        <div className="grid shrink-0 grid-cols-2 gap-3">
          <Button variant="outline" size="sm" onClick={() => choose("declined")}>{fr ? "Refuser" : "Decline"}</Button>
          <Button variant="outline" size="sm" onClick={() => choose("accepted")}>{fr ? "Accepter" : "Accept"}</Button>
        </div>
      </div>
    </section>
  );
};

export default CookieConsent;
